import React, { memo, useEffect, useState } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import { FormattedMessage } from '../../util/reactIntl';
import { IconSpinner } from '..';
import LiveCameraCapture from '../LiveCameraCapture/LiveCameraCapture';

import css from './FieldUploadImageWithCamera.module.css';

const RemoveImageButton = props => {
  const {
    className,
    rootClassName,
    onClick,
  } = props;

  const classes = classNames(rootClassName || css.removeImage, className);

  return (
    <button type="button" className={classes} onClick={onClick}>
      <svg
        width="10px"
        height="10px"
        viewBox="0 0 10 10"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g strokeWidth="1" fillRule="evenodd">
          <g transform="translate(-821.000000, -311.000000)">
            <g transform="translate(809.000000, 299.000000)">
              <path
                d="M21.5833333,16.5833333 L17.4166667,16.5833333 L17.4166667,12.4170833 C17.4166667,12.1866667 17.2391667,12 17.00875,12 C16.77875,12 16.5920833,12.18625 16.5920833,12.41625 L16.5883333,16.5833333 L12.4166667,16.5833333 C12.18625,16.5833333 12,16.7695833 12,17 C12,17.23 12.18625,17.4166667 12.4166667,17.4166667 L16.5875,17.4166667 L16.5833333,21.5829167 C16.5829167,21.8129167 16.7691667,21.9995833 16.9991667,22 L16.9995833,22 C17.2295833,22 17.41625,21.81375 17.4166667,21.58375 L17.4166667,17.4166667 L21.5833333,17.4166667 C21.8133333,17.4166667 22,17.23 22,17 C22,16.7695833 21.8133333,16.5833333 21.5833333,16.5833333"
                transform="translate(17.000000, 17.000000) rotate(-45.000000) translate(-17.000000, -17.000000) "
              />
            </g>
          </g>
        </g>
      </svg>
    </button>
  );
};

/**
 * FieldUploadImageWithCamera - Enhanced upload component with camera capture
 * 
 * Features:
 * - Upload from gallery (existing functionality)
 * - Capture from camera (new - fraud prevention)
 * - Video recording support
 * - Azure Blob Storage integration
 * 
 * @param {string} name - Field name
 * @param {string} id - Field ID
 * @param {string} label - Label for gallery upload button
 * @param {string} mode - 'photo' or 'video'
 * @param {boolean} enableCamera - Show camera option (default true)
 * @param {function} onUploadComplete - Callback after upload
 * @param {function} onImageRemove - Callback after removal
 * @param {boolean} isCompleted - Whether check-in/out is completed (disables edit)
 */
const FieldUploadImageWithCamera = props => {
  const {
    name,
    id,
    label,
    mode = 'photo',
    enableCamera = true,
    onUploadComplete,
    onImageRemove,
    isCompleted,
    ...rest
  } = props;

  const isDev = typeof window !== 'undefined' && window.location?.hostname === 'localhost';
  const [imageUrl, setImageUrl] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  return (
    <Field id={id} name={name} {...rest}>
      {fieldprops => {
        const { input: { onChange, value }, disabled: fieldDisabled } = fieldprops;

        useEffect(() => {
          if (value && !imageUrl) {
            (async () => {
              try {
                const response = await axios.get(
                  `${isDev ? 'http://localhost:3500' : ''}/api/azure-stream?fileName=${value}`
                );
                setImageUrl(response.data);
              } catch (error) {
                console.log(error);
              }
            })();
          }
        }, [value, imageUrl]);

        const onFileChange = async (event) => {
          setIsUploading(true);
          const formData = new FormData();
          formData.append('image', event.target.files[0], event.target.files[0].name);

          try {
            const resp = await axios.post(`${isDev ? 'http://localhost:3500' : ''}/api/azure-upload`, formData);
            const file = resp?.data?.file;
            onChange(file?.name);
            setImageUrl(file?.url);
            if (onUploadComplete) {
              onUploadComplete();
            }
          } catch (error) {
            console.log(error);
          } finally {
            setIsUploading(false);
          }
        };

        const handleCameraCapture = async (file) => {
          onChange(file?.name);
          setImageUrl(file?.url);
          setShowCamera(false);
          if (onUploadComplete) {
            onUploadComplete();
          }
        };

        const handleDeleteFile = async (e) => {
          e.stopPropagation();
          e.preventDefault();

          setIsUploading(true);

          try {
            const resp = await axios.get(
              `${isDev ? 'http://localhost:3500' : ''}/api/azure-delete?fileName=` + value,
              { responseType: 'blob', }
            );
            setImageUrl();
            if (onImageRemove) {
              onImageRemove();
            }
          } catch (error) {
            console.log(error);
          } finally {
            setIsUploading(false);
          }
        };

        // Camera capture view
        if (showCamera) {
          return (
            <LiveCameraCapture
              mode={mode}
              onCapture={handleCameraCapture}
              onCancel={() => setShowCamera(false)}
            />
          );
        }

        // Display uploaded file
        if (value && imageUrl) {
          const isVideo = mode === 'video' || value.endsWith('.webm') || value.endsWith('.mp4');
          
          return (
            <div className={css.fileContainer} title={isVideo ? 'play video' : 'view image'}>
              {isVideo ? (
                <video 
                  className={css.video} 
                  src={imageUrl} 
                  controls
                  onClick={() => window.open(imageUrl)} 
                />
              ) : (
                <img 
                  className={css.image} 
                  src={imageUrl} 
                  alt="uploaded file"
                  onClick={() => window.open(imageUrl)} 
                />
              )}

              {(!isCompleted) && (
                <RemoveImageButton onClick={handleDeleteFile} />
              )}
            </div>
          );
        }

        // Don't show upload options if completed
        if (isCompleted) {
          return null;
        }

        // Uploading state
        if (isUploading) {
          return (
            <div className={css.wrapper}>
              <div className={css.addFileContainer}>
                <IconSpinner />
              </div>
            </div>
          );
        }

        const inputProps = {
          id,
          name,
          onChange: onFileChange,
          type: "file",
          accept: mode === 'video' ? 'video/*' : 'image/*',
          disabled: fieldDisabled,
        };

        // Upload options (Camera + Gallery)
        return (
          <div className={css.wrapper}>
            {enableCamera && (
              <button
                type="button"
                className={css.cameraButton}
                onClick={() => setShowCamera(true)}
                disabled={fieldDisabled}
              >
                <svg className={css.cameraIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <FormattedMessage id={mode === 'video' ? 'FieldUploadImageWithCamera.recordVideo' : 'FieldUploadImageWithCamera.takePhoto'} />
              </button>
            )}
            
            <input
              className={css.hiddenFileInput}
              {...inputProps}
            />
            <label htmlFor={name} className={css.galleryButton}>
              <svg className={css.galleryIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {label || <FormattedMessage id={mode === 'video' ? 'FieldUploadImageWithCamera.chooseVideo' : 'FieldUploadImageWithCamera.choosePhoto'} />}
            </label>
          </div>
        );
      }}
    </Field>
  );
};

export default memo(FieldUploadImageWithCamera);
